import { useEffect, useRef } from 'react';

/**
 * Run a resync when the app returns to the foreground.
 *
 * iOS suspends timers and sockets in the background and does not implement
 * Page Lifecycle, so assume stale data on every foreground rather than trying
 * to detect it. `pageshow` is bound as well as `visibilitychange` because a
 * back-forward cache restore fires only the former.
 *
 * The callback is held in a ref so a new inline function on each render does
 * not rebind the listeners.
 */
export function useForegroundResync(onResync: () => void): void {
  const ref = useRef(onResync);

  useEffect(() => {
    ref.current = onResync;
  }, [onResync]);

  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') ref.current();
    };
    const onPageShow = (): void => {
      ref.current();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);
}
